import { useEffect, useMemo, useState } from "react";

import {
  Bar,
  Bubble,
  HorizontalBar,
  Line,
  Pie,
  Doughnut
} from "react-chartjs-2";

const MOBILE_MAX_WIDTH = 767.98;

/* Altura mínima por categoria nas barras horizontais. Com o maintainAspectRatio
   padrão do Chart.js (razão 2:1), um canvas de ~290px vira ~145px de altura —
   com 19 categorias sobram ~7px por linha e os rótulos do eixo Y se sobrepõem.
   No mobile a altura passa a ser função da quantidade de categorias. */
const MOBILE_ROW_HEIGHT = 24;
const MOBILE_MIN_HEIGHT = 200;

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= MOBILE_MAX_WIDTH
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_MAX_WIDTH);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return isMobile;
}

export default function Charts({
  type,
  title,
  legendX,
  legendY,
  maxScaleStep = 5,
  maxScaleValue,
  rowHeight,
  ...props
}) {
  const maxDataValue = useMemo(() => {
    if (props.data.datasets.length === 0) return 0;
    return Math.max(
      ...props.data.datasets.map(dataset => Math.max(...dataset.data))
    );
  }, [props.data.datasets]);

  const ceiling = useMemo(() => {
    if (maxScaleValue) return maxScaleValue;

    let ceil = Math.ceil(maxDataValue / maxScaleStep) * maxScaleStep;
    if (maxDataValue === maxScaleStep) ceil *= 1.5;
    return ceil > 10 ? ceil : 10;
  }, [maxDataValue, maxScaleStep]);

  const isMobile = useIsMobile();

  /* Só as barras horizontais: a altura delas depende do nº de categorias, não
     da largura. Vale sempre no mobile e, fora dele, para quem pedir com o prop
     `rowHeight` (px por categoria) — é o caso do relatório da avaliação
     diagnóstica, onde a lista de habilidades varia muito de tamanho. Sem o prop
     e fora do mobile nada muda: os defaults do Chart.js seguem valendo. */
  const heightByCategories = isMobile || !!rowHeight;

  const barHeight = useMemo(
    () =>
      Math.max(
        MOBILE_MIN_HEIGHT,
        (props.data?.labels?.length || 0) * (rowHeight || MOBILE_ROW_HEIGHT)
      ),
    [props.data?.labels?.length, rowHeight]
  );

  const barOverrides = heightByCategories ? { maintainAspectRatio: false } : {};

  /* Com maintainAspectRatio: false o Chart.js dimensiona o canvas pelo pai, que
     precisa ser bloco, com altura definida e position: relative.

     O width: 100% não é decoração: `.chart`, nas estatísticas, é um flex
     container com justify-content center. Sem largura declarada este wrapper
     encolhe para o conteúdo, o Chart.js mede um pai estreito e desenha um
     gráfico pequeno e centralizado. */
  const wrapBar = chart =>
    heightByCategories ? (
      <div style={{ position: "relative", width: "100%", height: barHeight }}>
        {chart}
      </div>
    ) : (
      chart
    );

  // As fontes fixas de 16–20px não cabem num canvas estreito.
  const titleFont = isMobile ? 14 : 20;
  const axisFont = isMobile ? 11 : 18;
  const axisFont2 = isMobile ? 11 : 16;
  const mobileTicks = isMobile ? { fontSize: 10 } : {};

  return (
    <>
      {type === "horizontalbar" ? (
        wrapBar(
          <HorizontalBar
            options={{
              ...barOverrides,
              title: {
                display: true,
                text: title,
                fontSize: titleFont
              },
              tooltips: {
                callbacks: {
                  label(value) {
                    return new Intl.NumberFormat("en-US", {
                      maximumSignificantDigits: 2
                    }).format(value.value);
                  }
                }
              },
              legend: {
                display: false,
                labels: {
                  font: {
                    size: 18
                  }
                }
              },
              scales: {
                yAxes: [
                  {
                    ticks: {
                      beginAtZero: true,
                      ...mobileTicks
                    },
                    scaleLabel: {
                      display: true,
                      labelString: legendY,
                      fontSize: axisFont
                    }
                  }
                ],
                xAxes: [
                  {
                    ticks: {
                      beginAtZero: true,
                      ...mobileTicks
                    },
                    scaleLabel: {
                      display: true,
                      labelString: legendX,
                      fontSize: axisFont
                    }
                  }
                ]
              },
              ...props.addOptions
            }}
            {...props}
          />
        )
      ) : type === "horizontalbar2" ? (
        wrapBar(
          <HorizontalBar
            options={{
              ...barOverrides,
              title: {
                display: true,
                text: title,
                fontSize: titleFont
              },
              legendY: {
                labels: {
                  display: false
                }
              },
              legend: {
                display: true,
                labels: {
                  font: {
                    size: 18
                  }
                }
              },
              scales: {
                yAxes: [
                  {
                    ticks: {
                      beginAtZero: true,
                      ...mobileTicks
                    },
                    scaleLabel: {
                      display: true,
                      labelString: legendY,
                      fontSize: axisFont2
                    }
                  }
                ],
                xAxes: [
                  {
                    ticks: {
                      beginAtZero: true,
                      ...mobileTicks
                    },
                    scaleLabel: {
                      display: true,
                      labelString: legendX,
                      fontSize: axisFont2
                    }
                  }
                ]
              },
              ...props.addOptions
            }}
            {...props}
          />
        )
      ) : type === "bar" ? (
        <Bar
          options={{
            title: {
              display: true,
              text: title,
              fontSize: 20
            },
            plugins: {
              decimation: {
                enabled: false
              }
            },
            legend: {
              display: true,
              labels: {
                font: {
                  size: 18
                }
              }
            },
            scales: {
              yAxes: [
                {
                  ticks: {
                    beginAtZero: true,
                    max: ceiling,
                    maxTicksLimit: ceiling + 1
                  },
                  scaleLabel: {
                    display: true,
                    labelString: legendY,
                    fontSize: 18
                  }
                }
              ],
              xAxes: [
                {
                  ticks: {
                    beginAtZero: true
                  },
                  scaleLabel: {
                    display: true,
                    labelString: legendX,
                    fontSize: 18
                  }
                }
              ]
            },
            ...props.addOptions
          }}
          {...props}
        />
      ) : type === "bar2" ? (
        <Bar
          options={{
            tooltips: { enabled: false },
            hover: { mode: null },
            title: {
              display: true,
              text: title,
              fontSize: 20
            },
            legend: {
              display: true,
              labels: {
                font: {
                  size: 18
                }
              }
            },
            scales: {
              yAxes: [
                {
                  ticks: {
                    beginAtZero: true,
                    max: ceiling,
                    maxTicksLimit: ceiling + 1
                  },
                  scaleLabel: {
                    fontSize: 18,
                    display: true,
                    labelString: legendY
                  }
                }
              ],
              xAxes: [
                {
                  ticks: {
                    beginAtZero: true
                  },
                  scaleLabel: {
                    fontSize: 18,
                    display: true,
                    labelString: legendX
                  }
                }
              ]
            },
            ...props.addOptions
          }}
          {...props}
        />
      ) : type === "bubble" ? (
        <Bubble
          options={{
            responsive: true,
            title: {
              display: false,
              text: title,
              fontSize: 20
            },
            legend: {
              position: "bottom"
            },
            layout: {
              padding: {
                left: 15,
                top: 15,
                right: 15,
                bottom: 15
              }
            },
            scales: {
              yAxes: [
                {
                  ticks: {
                    beginAtZero: true,
                    display: true
                  },
                  afterTickToLabelConversion(q) {
                    for (let tick in q.ticks) {
                      q.ticks[tick] = `Q${q.ticks.length - parseInt(tick)}`;
                    }
                  },
                  gridLines: {
                    drawBorder: true,
                    display: true
                  }
                }
              ],
              xAxes: [
                {
                  ticks: {
                    beginAtZero: true,
                    display: true
                  },
                  afterTickToLabelConversion(q) {
                    for (let tick in q.ticks) {
                      q.ticks[tick] = String.fromCharCode(65 + parseInt(tick));
                    }
                  },
                  gridLines: {
                    drawBorder: true,
                    display: true
                  }
                }
              ]
            },
            tooltips: {
              callbacks: {
                label(tooltipItem, data) {
                  let rLabel =
                    data.datasets[tooltipItem.datasetIndex].data[
                      tooltipItem.index
                    ].r;
                  return rLabel;
                }
              }
            },
            ...props.addOptions
          }}
          {...props}
        />
      ) : type === "pie" ? (
        <Pie
          options={{
            responsive: true,
            title: {
              display: true,
              text: title,
              fontSize: 20
            }
          }}
        />
      ) : type === "line" ? (
        <Line
          options={{
            responsive: true,
            title: {
              display: true,
              text: title,
              fontSize: 18
            },
            scales: {
              yAxes: [
                {
                  ticks: {
                    beginAtZero: true
                  },
                  scaleLabel: {
                    display: true,
                    labelString: legendY
                  }
                }
              ],
              xAxes: [
                {
                  ticks: {
                    beginAtZero: true
                  },
                  scaleLabel: {
                    display: true,
                    labelString: legendX
                  }
                }
              ]
            },
            legend: {
              display: false
            }
          }}
          {...props}
        />
      ) : type === "doughnut " ? (
        <Doughnut {...props} />
      ) : null}
    </>
  );
}
